import React, { useEffect, useState } from 'react';
import {
    Eye, EyeOff, Plus, Trash2, Zap, MessageCircle, Settings,
    ChevronRight, Pencil, Check, X, Square, Clock, RotateCcw,
    Users, Smartphone, Shield, Search, ExternalLink
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ContactCard } from './ContactCard';
import { Login } from './Login';
import { socket } from '../lib/socket';
import { ConnectionState, Platform } from '../types/connection';
import { useNavigate } from 'react-router-dom';

type ProbeMethod = 'delete' | 'reaction';
type TimeRange = '1h' | '24h' | '7d' | '30d';

interface DashboardProps {
    connectionState: ConnectionState;
}

interface TrackerData {
    rtt: number;
    avg: number;
    median: number;
    threshold: number;
    state: string;
    timestamp: number;
    deviceJid?: string;
}

interface DeviceInfo {
    jid: string;
    state: string;
    rtt: number;
    avg: number;
}

interface ContactInfo {
    dbId: number | null;
    jid: string;
    displayNumber: string;
    contactName: string;
    customName: string | null;
    data: TrackerData[];
    devices: DeviceInfo[];
    deviceCount: number;
    presence: string | null;
    profilePic: string | null;
    platform: Platform;
    knownDevices: string[];
    selectedDevice: string | null;
}

function getBestDevice(devices: DeviceInfo[]): DeviceInfo | null {
    if (!Array.isArray(devices) || devices.length === 0) return null;
    const rank = (state: string) => {
        if (state.includes('Online')) return 0;
        if (state === 'Standby') return 1;
        if (state.includes('Calibrating')) return 2;
        if (state === 'OFFLINE') return 3;
        return 4;
    };
    return [...devices].sort((a, b) => rank(a.state) - rank(b.state))[0] ?? null;
}

function StatusDot({ status }: { status: string }) {
    const color = status.includes('Online')
        ? 'bg-emerald-400 shadow-emerald-400/50'
        : status === 'Standby'
            ? 'bg-amber-400 shadow-amber-400/50'
            : status === 'OFFLINE'
                ? 'bg-red-400 shadow-red-400/50'
                : 'bg-white/20';

    return (
        <span className={`relative flex h-2.5 w-2.5`}>
            {status.includes('Online') && (
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-40 ${color}`} />
            )}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 shadow-lg ${color}`} />
        </span>
    );
}

function StatusText({ status }: { status: string }) {
    const colorClass = status.includes('Online')
        ? 'text-emerald-400'
        : status === 'Standby'
            ? 'text-amber-400'
            : status === 'OFFLINE'
                ? 'text-red-400'
                : status.includes('Calibrating')
                    ? 'text-cyan-400'
                    : 'text-white/40';

    return <span className={`text-sm font-medium ${colorClass}`}>{status}</span>;
}

export function Dashboard({ connectionState }: DashboardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [inputNumber, setInputNumber] = useState('');
    const [selectedPlatform, setSelectedPlatform] = useState<Platform>(
        connectionState.whatsapp ? 'whatsapp' : 'signal'
    );
    const [contacts, setContacts] = useState<Map<string, ContactInfo>>(new Map());
    const [error, setError] = useState<string | null>(null);
    const [privacyMode, setPrivacyMode] = useState(false);
    const [probeMethod, setProbeMethod] = useState<ProbeMethod>('delete');
    const [showConnections, setShowConnections] = useState(false);
    const [expandedContacts, setExpandedContacts] = useState<Set<string>>(new Set());
    const [editingNameJid, setEditingNameJid] = useState<string | null>(null);
    const [nameDraft, setNameDraft] = useState('');
    const [timeRange, setTimeRange] = useState<TimeRange>('24h');

    useEffect(() => {
        function onTrackerUpdate(update: any) {
            const { jid, ...data } = update;
            if (!jid) return;

            setContacts(prev => {
                const next = new Map(prev);
                const contact = next.get(jid);

                if (contact) {
                    const updatedContact = { ...contact };

                    if (data.presence !== undefined) {
                        updatedContact.presence = data.presence;
                    }
                    if (data.deviceCount !== undefined) {
                        updatedContact.deviceCount = data.deviceCount;
                    }
                    if (data.devices !== undefined) {
                        updatedContact.devices = data.devices;
                    }

                    if (data.median !== undefined && data.devices && data.devices.length > 0) {
                        const now = Date.now();
                        const newPoints: TrackerData[] = (data.devices as DeviceInfo[]).map((d: DeviceInfo) => ({
                            rtt: d.rtt,
                            avg: d.avg,
                            median: data.median,
                            threshold: data.threshold,
                            state: d.state,
                            timestamp: now,
                            deviceJid: d.jid
                        }));

                        const newDeviceJids = new Set((data.devices as DeviceInfo[]).map((d: DeviceInfo) => d.jid));
                        const updatedKnown = new Set(updatedContact.knownDevices);
                        newDeviceJids.forEach(dj => updatedKnown.add(dj));
                        updatedContact.knownDevices = Array.from(updatedKnown);

                        updatedContact.data = [...updatedContact.data, ...newPoints];
                    }

                    next.set(jid, updatedContact);
                }

                return next;
            });
        }

        function onProfilePic(data: { jid: string, url: string | null }) {
            setContacts(prev => {
                const next = new Map(prev);
                const contact = next.get(data.jid);
                if (contact) {
                    next.set(data.jid, { ...contact, profilePic: data.url });
                }
                return next;
            });
        }

        function onContactName(data: { jid: string, name: string }) {
            setContacts(prev => {
                const next = new Map(prev);
                const contact = next.get(data.jid);
                if (contact) {
                    next.set(data.jid, { ...contact, contactName: data.name });
                }
                return next;
            });
        }

        function onContactAdded(data: { jid: string, dbId?: number, number: string, platform?: Platform, customName?: string | null }) {
            setContacts(prev => {
                const next = new Map(prev);
                next.set(data.jid, {
                    dbId: data.dbId ?? null,
                    jid: data.jid,
                    displayNumber: data.number,
                    contactName: data.number,
                    customName: data.customName ?? null,
                    data: [],
                    devices: [],
                    deviceCount: 0,
                    presence: null,
                    profilePic: null,
                    platform: data.platform || 'whatsapp',
                    knownDevices: [],
                    selectedDevice: null
                });
                return next;
            });
            setInputNumber('');
        }

        function onContactRemoved(jid: string) {
            setContacts(prev => {
                const next = new Map(prev);
                next.delete(jid);
                return next;
            });
        }

        function onError(data: { jid?: string, message: string }) {
            setError(data.message);
            setTimeout(() => setError(null), 3050);
        }

        function onContactNameUpdated(data: { jid: string; customName: string | null }) {
            setContacts(prev => {
                const next = new Map(prev);
                const contact = next.get(data.jid);
                if (!contact) return prev;
                next.set(data.jid, { ...contact, customName: data.customName });
                return next;
            });
        }

        function onProbeMethod(method: ProbeMethod) {
            setProbeMethod(method);
        }

        function onTrackedContacts(contacts: { id: string, dbId?: number, platform: Platform, number?: string, customName?: string | null, profilePicUrl?: string | null, lastKnownState?: string | null, lastKnownAvgRtt?: number | null, lastKnownRtt?: number | null }[]) {
            setContacts(prev => {
                const next = new Map(prev);
                contacts.forEach(({ id, dbId, platform, number, customName, profilePicUrl, lastKnownState, lastKnownAvgRtt }) => {
                    let displayNumber = number || id;
                    if (!number) {
                        if (platform === 'signal') {
                            displayNumber = id.replace('signal:', '');
                        } else {
                            displayNumber = id.split('@')[0];
                        }
                    }

                    const existing = next.get(id);
                    if (existing) {
                        next.set(id, {
                            ...existing,
                            dbId: dbId ?? existing.dbId,
                            displayNumber,
                            contactName: existing.contactName || displayNumber,
                            customName: customName ?? existing.customName,
                            profilePic: profilePicUrl ?? existing.profilePic
                        });
                    } else {
                        next.set(id, {
                            dbId: dbId ?? null,
                            jid: id,
                            displayNumber,
                            contactName: displayNumber,
                            customName: customName ?? null,
                            data: [],
                            devices: lastKnownState ? [{ jid: id, state: lastKnownState, rtt: lastKnownAvgRtt ?? 0, avg: lastKnownAvgRtt ?? 0 }] : [],
                            deviceCount: lastKnownState ? 1 : 0,
                            presence: null,
                            profilePic: profilePicUrl ?? null,
                            platform,
                            knownDevices: [],
                            selectedDevice: null
                        });
                    }

                    socket.emit('get-contact-history', { jid: id, range: '24h' });
                });
                return next;
            });
        }

        function onContactHistory(data: { jid: string; range: string; samples: TrackerData[]; knownDevices?: string[]; deviceJid?: string | null }) {
            if (!data?.jid || !Array.isArray(data.samples)) return;
            setContacts(prev => {
                const next = new Map(prev);
                const contact = next.get(data.jid);
                if (!contact) return prev;

                const historicalData = data.samples.map(s => ({
                    rtt: s.rtt,
                    avg: s.avg,
                    median: s.median,
                    threshold: s.threshold,
                    state: s.state,
                    timestamp: s.timestamp,
                    deviceJid: s.deviceJid
                }));

                const existingLiveTimestamps = new Set(contact.data.map(d => d.timestamp));
                const merged = [
                    ...historicalData.filter(h => !existingLiveTimestamps.has(h.timestamp)),
                    ...contact.data
                ].sort((a, b) => a.timestamp - b.timestamp);

                const updatedContact = { ...contact, data: merged };
                if (Array.isArray(data.knownDevices) && data.knownDevices.length > 0) {
                    updatedContact.knownDevices = data.knownDevices;
                }
                next.set(data.jid, updatedContact);
                return next;
            });
        }

        function onContactStatsReset(jid: string) {
            setContacts(prev => {
                const next = new Map(prev);
                const contact = next.get(jid);
                if (!contact) return prev;
                next.set(jid, { ...contact, data: [], knownDevices: [], selectedDevice: null });
                return next;
            });
        }

        socket.on('tracker-update', onTrackerUpdate);
        socket.on('profile-pic', onProfilePic);
        socket.on('contact-name', onContactName);
        socket.on('contact-added', onContactAdded);
        socket.on('contact-removed', onContactRemoved);
        socket.on('contact-name-updated', onContactNameUpdated);
        socket.on('error', onError);
        socket.on('probe-method', onProbeMethod);
        socket.on('tracked-contacts', onTrackedContacts);
        socket.on('contact-history', onContactHistory);
        socket.on('contact-stats-reset', onContactStatsReset);

        socket.emit('get-tracked-contacts');

        return () => {
            socket.off('tracker-update', onTrackerUpdate);
            socket.off('profile-pic', onProfilePic);
            socket.off('contact-name', onContactName);
            socket.off('contact-added', onContactAdded);
            socket.off('contact-removed', onContactRemoved);
            socket.off('contact-name-updated', onContactNameUpdated);
            socket.off('error', onError);
            socket.off('probe-method', onProbeMethod);
            socket.off('tracked-contacts', onTrackedContacts);
            socket.off('contact-history', onContactHistory);
            socket.off('contact-stats-reset', onContactStatsReset);
        };
    }, []);

    const handleAdd = () => {
        if (!inputNumber) return;
        socket.emit('add-contact', { number: inputNumber, platform: selectedPlatform });
    };

    const handleRemove = (jid: string) => {
        socket.emit('remove-contact', jid);
    };

    const handleProbeMethodChange = (method: ProbeMethod) => {
        socket.emit('set-probe-method', method);
    };

    const handleTimeRangeChange = (range: TimeRange) => {
        setTimeRange(range);
        contacts.forEach((contact, jid) => {
            socket.emit('get-contact-history', { jid, range, deviceJid: contact.selectedDevice ?? undefined });
        });
    };

    const handleDeviceChange = (jid: string, deviceJid: string | null) => {
        setContacts(prev => {
            const next = new Map(prev);
            const contact = next.get(jid);
            if (!contact) return prev;
            next.set(jid, { ...contact, selectedDevice: deviceJid });
            return next;
        });
        socket.emit('get-contact-history', { jid, range: timeRange, deviceJid: deviceJid ?? undefined });
    };

    const handleResetStats = (jid: string) => {
        if (!window.confirm(t('dashboard.resetConfirm'))) return;
        socket.emit('reset-contact-stats', jid);
    };

    const toggleContactExpanded = (jid: string) => {
        setExpandedContacts(prev => {
            const next = new Set(prev);
            if (next.has(jid)) {
                next.delete(jid);
            } else {
                next.add(jid);
            }
            return next;
        });
    };

    const startEditName = (jid: string, currentName: string) => {
        setEditingNameJid(jid);
        setNameDraft(currentName);
    };

    const cancelEditName = () => {
        setEditingNameJid(null);
        setNameDraft('');
    };

    const saveCustomName = (jid: string) => {
        const trimmed = nameDraft.trim();
        const customName = trimmed.length > 0 ? trimmed : null;
        setContacts(prev => {
            const next = new Map(prev);
            const contact = next.get(jid);
            if (!contact) return prev;
            next.set(jid, {
                ...contact,
                customName
            });
            return next;
        });
        socket.emit('set-contact-name', { jid, name: customName });
        cancelEditName();
    };

    const getDisplayName = (contact: ContactInfo) => contact.customName || contact.contactName || contact.displayNumber;

    const getCurrentStatus = (contact: ContactInfo) => {
        if (contact.selectedDevice) {
            const selected = contact.devices.find(d => d.jid === contact.selectedDevice);
            if (selected) return selected.state;
        }
        return getBestDevice(contact.devices)?.state ?? 'Unknown';
    };

    const getFilteredData = (contact: ContactInfo): TrackerData[] => {
        const rangeMs: Record<TimeRange, number> = {
            '1h': 60 * 60 * 1000,
            '24h': 24 * 60 * 60 * 1000,
            '7d': 7 * 24 * 60 * 60 * 1000,
            '30d': 30 * 24 * 60 * 60 * 1000
        };
        const cutoff = Date.now() - rangeMs[timeRange];
        let filtered = contact.data.filter(d => d.timestamp >= cutoff);
        if (contact.selectedDevice) {
            filtered = filtered.filter(d => d.deviceJid === contact.selectedDevice);
        }
        return filtered;
    };

    const timeRangeLabels: Record<TimeRange, string> = {
        '1h': t('dashboard.1h'),
        '24h': t('dashboard.24h'),
        '7d': t('dashboard.7d'),
        '30d': t('dashboard.30d')
    };

    const contactsArray = Array.from(contacts.values());

    return (
        <div className="space-y-6">
            {/* Control Panel */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                {/* Top row: title + controls */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                            <Users size={20} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">{t('dashboard.trackContacts')}</h2>
                            <p className="text-xs text-white/40">{contactsArray.length} contacts tracked</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Manage Connections */}
                        <button
                            onClick={() => setShowConnections(!showConnections)}
                            className={`px-3.5 py-2 text-sm font-medium rounded-xl transition-all duration-200 flex items-center gap-2 ${
                                showConnections
                                    ? 'bg-white/10 text-white border border-white/20'
                                    : 'bg-white/[0.05] text-white/60 hover:bg-white/[0.08] hover:text-white/80 border border-white/5'
                            }`}
                        >
                            <Settings size={14} />
                            {showConnections ? t('dashboard.hideConnections') : t('dashboard.manageConnections')}
                        </button>

                        {/* Probe Method Pills */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-white/40 font-medium">{t('dashboard.probeMethod')}</span>
                            <div className="flex rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]">
                                <button
                                    onClick={() => handleProbeMethodChange('delete')}
                                    className={`px-3.5 py-2 text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 ${
                                        probeMethod === 'delete'
                                            ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/25'
                                            : 'text-white/50 hover:text-white/70 hover:bg-white/[0.05]'
                                    }`}
                                    title="Silent Delete Probe"
                                >
                                    <Trash2 size={12} />
                                    Delete
                                </button>
                                <button
                                    onClick={() => handleProbeMethodChange('reaction')}
                                    className={`px-3.5 py-2 text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 ${
                                        probeMethod === 'reaction'
                                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25'
                                            : 'text-white/50 hover:text-white/70 hover:bg-white/[0.05]'
                                    }`}
                                    title="Reaction Probe"
                                >
                                    <Zap size={12} />
                                    Reaction
                                </button>
                            </div>
                        </div>

                        {/* Privacy Toggle */}
                        <button
                            onClick={() => setPrivacyMode(!privacyMode)}
                            className={`px-3.5 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition-all duration-200 border ${
                                privacyMode
                                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20 shadow-lg shadow-emerald-500/10'
                                    : 'bg-white/[0.05] text-white/50 border-white/10 hover:bg-white/[0.08] hover:text-white/70'
                            }`}
                            title={privacyMode ? 'Privacy Mode: ON' : 'Privacy Mode: OFF'}
                        >
                            {privacyMode ? <EyeOff size={16} /> : <Eye size={16} />}
                            <span>{privacyMode ? t('dashboard.privacyOn') : t('dashboard.privacyOff')}</span>
                        </button>
                    </div>
                </div>

                {/* Input row */}
                <div className="flex gap-3">
                    {/* Platform Tabs */}
                    <div className="flex rounded-xl overflow-hidden border border-white/10 bg-white/[0.03] shrink-0">
                        <button
                            onClick={() => setSelectedPlatform('whatsapp')}
                            disabled={!connectionState.whatsapp}
                            className={`px-4 py-2.5 text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                                selectedPlatform === 'whatsapp'
                                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/25'
                                    : connectionState.whatsapp
                                        ? 'text-white/50 hover:text-white/70 hover:bg-white/[0.05]'
                                        : 'text-white/20 cursor-not-allowed'
                            }`}
                            title={connectionState.whatsapp ? 'WhatsApp' : 'WhatsApp not connected'}
                        >
                            <MessageCircle size={16} />
                            WhatsApp
                        </button>
                        <button
                            onClick={() => setSelectedPlatform('signal')}
                            disabled={!connectionState.signal}
                            className={`px-4 py-2.5 text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                                selectedPlatform === 'signal'
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
                                    : connectionState.signal
                                        ? 'text-white/50 hover:text-white/70 hover:bg-white/[0.05]'
                                        : 'text-white/20 cursor-not-allowed'
                            }`}
                            title={connectionState.signal ? 'Signal' : 'Signal not connected'}
                        >
                            <Shield size={16} />
                            Signal
                        </button>
                    </div>

                    <div className="relative flex-1">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                        <input
                            type="text"
                            placeholder={t('dashboard.placeholder')}
                            className="w-full pl-11 pr-4 py-2.5 bg-white/[0.05] border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 outline-none transition-all"
                            value={inputNumber}
                            onChange={(e) => setInputNumber(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
                        />
                    </div>

                    <button
                        onClick={handleAdd}
                        className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl hover:from-emerald-400 hover:to-cyan-400 flex items-center gap-2 font-semibold transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 shrink-0"
                    >
                        <Plus size={18} /> {t('dashboard.addContact')}
                    </button>
                </div>

                {/* Error */}
                {error && (
                    <div className="mt-3 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                        <X size={14} />
                        {error}
                    </div>
                )}
            </div>

            {/* Connections Panel */}
            {showConnections && (
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm">
                    <Login connectionState={connectionState} />
                </div>
            )}

            {/* Time Range Filter */}
            {contacts.size > 0 && (
                <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-2xl px-5 py-3.5 backdrop-blur-sm">
                    <Clock size={16} className="text-white/30" />
                    <span className="text-sm text-white/40 font-medium mr-1">{t('dashboard.timeRange')}</span>
                    <div className="flex gap-2">
                        {(['1h', '24h', '7d', '30d'] as TimeRange[]).map(range => (
                            <button
                                key={range}
                                onClick={() => handleTimeRangeChange(range)}
                                className={`px-4 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                                    timeRange === range
                                        ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-lg shadow-emerald-500/25'
                                        : 'bg-white/[0.05] text-white/50 hover:bg-white/[0.08] hover:text-white/70'
                                }`}
                            >
                                {timeRangeLabels[range]}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Contact List */}
            {contacts.size === 0 ? (
                <div className="bg-white/[0.03] border border-white/10 border-dashed rounded-2xl p-16 text-center backdrop-blur-sm">
                    <div className="w-16 h-16 rounded-2xl bg-white/[0.05] flex items-center justify-center mx-auto mb-5">
                        <Users size={28} className="text-white/20" />
                    </div>
                    <p className="text-white/50 text-lg font-medium">{t('dashboard.noContacts')}</p>
                    <p className="text-white/30 text-sm mt-2">{t('dashboard.noContactsSub')}</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {contactsArray.map(contact => {
                        const isExpanded = expandedContacts.has(contact.jid);
                        const status = getCurrentStatus(contact);

                        return (
                            <div
                                key={contact.jid}
                                className={`bg-white/[0.03] border rounded-2xl overflow-hidden backdrop-blur-sm transition-all duration-300 ${
                                    isExpanded ? 'border-white/20 shadow-xl shadow-black/20' : 'border-white/10 hover:border-white/15'
                                }`}
                            >
                                {/* Contact Header Row */}
                                <button
                                    onClick={() => toggleContactExpanded(contact.jid)}
                                    className="w-full text-left px-5 py-4 hover:bg-white/[0.02] transition-colors"
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        {/* Left: Avatar + Info */}
                                        <div className="flex items-center gap-4 min-w-0 flex-1">
                                            {/* Chevron */}
                                            <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                                                <ChevronRight size={16} className="text-white/30" />
                                            </div>

                                            {/* Avatar with status dot */}
                                            <div className="relative shrink-0">
                                                <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden ring-2 ring-white/5">
                                                    {contact.profilePic ? (
                                                        <img
                                                            src={contact.profilePic}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-white/40 text-sm font-bold">
                                                            {getDisplayName(contact)[0]?.toUpperCase() ?? '?'}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-slate-950 ${
                                                    status.includes('Online') ? 'bg-emerald-400' :
                                                    status === 'Standby' ? 'bg-amber-400' :
                                                    status === 'OFFLINE' ? 'bg-red-400' : 'bg-white/20'
                                                }`} />
                                            </div>

                                            {/* Name + number + platform */}
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-white truncate">
                                                        {privacyMode ? getDisplayName(contact).replace(/\d/g, '•') : getDisplayName(contact)}
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                                        contact.platform === 'whatsapp'
                                                            ? 'bg-emerald-500/15 text-emerald-400'
                                                            : 'bg-blue-500/15 text-blue-400'
                                                    }`}>
                                                        {contact.platform === 'whatsapp' ? 'WA' : 'SIG'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 mt-0.5">
                                                    <span className="text-xs text-white/35 truncate">
                                                        {privacyMode ? contact.displayNumber.replace(/\d/g, '•') : contact.displayNumber}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right: Status + Controls */}
                                        <div className="flex items-center gap-3 shrink-0">
                                            {/* Live Status */}
                                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03]">
                                                <StatusDot status={status} />
                                                <StatusText status={status} />
                                            </div>

                                            {/* Device count */}
                                            <div className="flex items-center gap-1.5 text-xs text-white/40">
                                                <Smartphone size={12} />
                                                <span>{contact.deviceCount}</span>
                                            </div>

                                            {/* Action buttons */}
                                            {editingNameJid === contact.jid ? (
                                                <div
                                                    className="flex items-center gap-2"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <input
                                                        type="text"
                                                        className="px-3 py-1.5 bg-white/[0.08] border border-white/15 rounded-lg text-sm text-white placeholder-white/30 w-40 outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/40"
                                                        value={nameDraft}
                                                        onChange={(e) => setNameDraft(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveCustomName(contact.jid);
                                                            if (e.key === 'Escape') cancelEditName();
                                                        }}
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() => saveCustomName(contact.jid)}
                                                        className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors"
                                                        title="Save name"
                                                    >
                                                        <Check size={14} />
                                                    </button>
                                                    <button
                                                        onClick={cancelEditName}
                                                        className="p-1.5 rounded-lg bg-white/[0.05] text-white/50 hover:bg-white/[0.1] hover:text-white/70 transition-colors"
                                                        title="Cancel"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startEditName(contact.jid, getDisplayName(contact));
                                                        }}
                                                        className="p-2 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.05] transition-all"
                                                        title="Rename"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (contact.dbId) {
                                                                navigate(`/dashboard/contact/${contact.dbId}`);
                                                            }
                                                        }}
                                                        disabled={!contact.dbId}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 bg-gradient-to-r from-cyan-500/15 to-blue-500/15 text-cyan-400 hover:from-cyan-500/25 hover:to-blue-500/25 border border-cyan-500/20 disabled:opacity-30 disabled:cursor-not-allowed"
                                                        title="View detailed stats"
                                                    >
                                                        <ExternalLink size={12} />
                                                        {t('dashboard.detail')}
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemove(contact.jid);
                                                        }}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all duration-200 flex items-center gap-1.5"
                                                        title="Stop tracking"
                                                    >
                                                        <Square size={10} />
                                                        {t('dashboard.stop')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <div className="px-5 pb-5 border-t border-white/5">
                                        {/* Device selector + Reset */}
                                        <div className="flex items-center gap-3 py-4">
                                            {contact.knownDevices.length > 0 && (
                                                <div className="flex items-center gap-2.5">
                                                    <Smartphone size={14} className="text-cyan-400" />
                                                    <span className="text-xs text-white/40 font-medium">{t('dashboard.device')}</span>
                                                    <select
                                                        value={contact.selectedDevice ?? ''}
                                                        onChange={(e) => handleDeviceChange(contact.jid, e.target.value || null)}
                                                        className="text-sm bg-white/[0.05] border border-white/10 rounded-lg px-3 py-1.5 text-white focus:ring-2 focus:ring-emerald-500/50 outline-none appearance-none cursor-pointer hover:bg-white/[0.08] transition-colors"
                                                    >
                                                        <option value="" className="bg-slate-900 text-white">
                                                            {t('dashboard.allDevices')}
                                                        </option>
                                                        {contact.knownDevices.map((dj, idx) => (
                                                            <option key={dj} value={dj} className="bg-slate-900 text-white">
                                                                Device {idx + 1} ({dj.split('@')[0].slice(-6)})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                            <button
                                                onClick={() => handleResetStats(contact.jid)}
                                                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 transition-all duration-200"
                                                title="Reset stats"
                                            >
                                                <RotateCcw size={12} /> {t('dashboard.resetStats')}
                                            </button>
                                        </div>

                                        <ContactCard
                                            jid={contact.jid}
                                            displayNumber={getDisplayName(contact)}
                                            data={getFilteredData(contact)}
                                            devices={contact.devices}
                                            deviceCount={contact.deviceCount}
                                            presence={contact.presence}
                                            profilePic={contact.profilePic}
                                            onRemove={() => handleRemove(contact.jid)}
                                            privacyMode={privacyMode}
                                            platform={contact.platform}
                                            showHeader={false}
                                            selectedDevice={contact.selectedDevice}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
